import { Button, message, Modal, Typography } from 'antd';
import Tooltip, { TooltipPlacement } from 'antd/lib/tooltip';
import { useRbac } from 'features/rbac/rbac';
import { ERbacPermissions } from 'features/rbac/rbacPermissionsList';
import { IActiveListing } from 'models/interfaces';
import React, { Fragment, useState } from 'react';
import autoAdjustmentEnabled from '../../../../../../assets/images/enabled-roundel.svg';
import autoAdjustmentDisabled from '../../../../../../assets/images/disabled-roundel.svg';
import { ZonesService } from 'services/SkyboxServices';
import { LoggerService } from 'sharedServices';
import ActiveListingPoCard from '../ActiveListingPoCard';

const AutoAdjustmentStatusIcon: React.FC<{ enabled: boolean }> = ({
  enabled,
}) => (
  <img
    src={enabled ? autoAdjustmentEnabled : autoAdjustmentDisabled}
    alt={enabled ? 'auto-adjust-enabled' : 'auto-adjust-disabled'}
  />
);

const ListingAutoPriceAdjustmentStatus: React.FC<{
  placement?: TooltipPlacement;
  activeListingData: IActiveListing;
  setUpdatedAutoAdjustmentStatus: Function;
  eventId: number;
  eventName: string;
  eventDate: string;
  venueName: string;
  venueLocation: string;
}> = ({ 
  placement, 
  activeListingData, 
  setUpdatedAutoAdjustmentStatus,
  eventId,
  eventName,
  eventDate,
  venueName,
  venueLocation, 
}) => {
  const [openAutoAdjustmentConfirmBox, setOpenAutoAdjustmentConfirmBox] =
    useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { hasPermissions } = useRbac();

  function parseErrorMessage(text?: string): string[] {
    if (!text) return ['Something went wrong.'];
    return text
      .split(';') 
      .map((line) => line.trim()) 
      .filter((line) => line.length > 0); 
  }

  const updateAutoAdjustment = async (data: IActiveListing) => {
    if (data) {
      setIsUpdatingStatus(true);
      const updatingAutoAdjustmentStatusKey = 'updatingBroadcastStatusKey';
      message.loading({
        content: 'Updating Auto adjustment status...',
        duration: 0,
        key: updatingAutoAdjustmentStatusKey,
        className: 'toastMsg loadingMsg',
      });

      let isAutoAdjustmentStatusUpdated = false;
      let updatedStatusResult = null;

      try {
        updatedStatusResult = await new ZonesService().updateAutoAdjustment(
          data.inventory_id,
          !data.auto_adjustment,
        );

        isAutoAdjustmentStatusUpdated = true;

        message.success({
          content: 'Auto adjustment status has been updated successfully!',
          key: updatingAutoAdjustmentStatusKey,
          duration: 3,
          className: 'toastMsg savedSuccess',
        });
      } catch (error:any) {
        const errorMessage = error?.error?.response?.data?.message;
        const errorParts = parseErrorMessage(errorMessage);

        errorParts.forEach((msg, idx) => {
          message.error({
            content: msg,
            duration: 5,
            key: idx === 0 ? updatingAutoAdjustmentStatusKey : `${updatingAutoAdjustmentStatusKey}-${idx}`,
            className: 'toastMsg savedFailed',
          });
        });

        new LoggerService().log({
          payload: error,
          function_name: 'updateAutoAdjustment',
        });

        setOpenAutoAdjustmentConfirmBox(false);
        setIsUpdatingStatus(false);
      }

      if (isAutoAdjustmentStatusUpdated) {
        setUpdatedAutoAdjustmentStatus(
          updatedStatusResult?.data.data.inventory_id,
          updatedStatusResult?.data.data.auto_adjustment_status,
        );

        setOpenAutoAdjustmentConfirmBox(false);
        setIsUpdatingStatus(false);
      }
    }
  };

  return (
    <Fragment>
      {placement ? (
        <Tooltip title="Change Auto Adjustment Status" placement={placement}>
          <Typography.Link
            disabled={!activeListingData.broadcast}
            onClick={() => {
              if (
                hasPermissions([
                  ERbacPermissions.ZONES_ACTIVE_LISTING_BROADCAST_ACTION,
                ])
              ) {
                setOpenAutoAdjustmentConfirmBox(true);
              }
            }}
          >
            <AutoAdjustmentStatusIcon
              enabled={!!activeListingData.auto_adjustment}
            />
          </Typography.Link>
        </Tooltip>
      ) : (
        <Tooltip title="Change Auto Adjustment Status">
          <Typography.Link
            disabled={!activeListingData.broadcast}
            onClick={() => {
              if (
                hasPermissions([
                  ERbacPermissions.ZONES_ACTIVE_LISTING_BROADCAST_ACTION,
                ])
              ) {
                setOpenAutoAdjustmentConfirmBox(true);
              }
            }}
          >
            <AutoAdjustmentStatusIcon
              enabled={!!activeListingData.auto_adjustment}
            />
          </Typography.Link>
        </Tooltip>
      )}
      <Modal
        closable={false}
        width={360}
        title={false}
        footer={false}
        visible={openAutoAdjustmentConfirmBox}
      >
        <div className="modalHeader modalHeaderCenter">
          Are you sure you want to{' '}
          <span>
            {activeListingData.auto_adjustment
              ? 'turn off '
              : 'turn on '}
          </span>{' '}
          automatic price adjustment?
        </div>
        <div className="modalActiveListingPoCard">
          {/* {isPoLineCard && splitData ? (
              <ActiveListingPoLineCard
                splitData={splitData}
              />
            ) : ( */}
              <ActiveListingPoCard
                eventId={eventId}
                eventName={eventName}
                eventDate={eventDate}
                venueName={venueName}
                venueLocation={venueLocation}
                activeListingData={activeListingData}
                showCardActionHeader={false}
                disableSplitToggle={true}
              />
            {/* )} */}
          </div>
        <div className="modalCustomFooter">
          <Button
            className="linkBtn linkBtnColor customLinkBtn"
            onClick={() => {
              setOpenAutoAdjustmentConfirmBox(false);
            }}
            disabled={isUpdatingStatus}
          >
            CANCEL
          </Button>
          <Button
            className="btnOk"
            disabled={isUpdatingStatus}
            onClick={() => updateAutoAdjustment(activeListingData)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
};

export default ListingAutoPriceAdjustmentStatus;
